export type CaseStatus = 'new' | 'analysing' | 'unresolved' | 'likely_explained' | 'explained' | 'resolved';
export type Scope = 'global' | 'national' | 'regional' | 'local';

export interface CaseSummary {
  id: string;
  case_number: string;
  status: CaseStatus;
  classification: string | null;
  witness_count: number;
  occurred_at: string;
  submitted_at?: string;
  latitude: number;
  longitude: number;
  location_name: string | null;
  country_code?: string | null;
  shape: string | null;
  colour: string | null;
  has_media: boolean;
  /** Set on historic cases added from the admin. */
  historic_title?: string;
  historic_teaser?: string;
  historic_short_location?: string;
  historic_year?: number;
}

export interface WeatherSnapshot {
  temperature_c: string | number | null;
  cloud_cover: number | null;
  visibility_km: string | number | null;
  precipitation_mm: string | number | null;
  wind_speed_kmh: string | number | null;
  wind_direction_deg?: number | null;
  source: string | null;
  fetched_at: string | null;
}

export interface CaseComment { id: string; report_id: string; user_id: string; username: string; body: string; created_at: string }
export interface Corroboration { id: string; report_id: string; user_id: string; username?: string; description?: string | null; occurred_at?: string | null; created_at?: string }
export interface ReportMatch { id: string; type: string; name: string | null; reference: string; confidence: number | null; metadata: Record<string, unknown> }
export interface ReportAnalysis { summary: string | null; classification: string | null; confidence: number | null; model: string | null; created_at: string | null; astronomy_score: number | null; weather_score: number | null; drone_score: number | null; balloon_score: number | null; aircraft_score?: number | null; satellite_score?: number | null; }

export interface CaseDetail extends CaseSummary {
  history?: Array<{ id: string; label: string; occurred_at: string }>;
  enrichment_status?: 'queued' | 'running' | 'complete' | 'failed' | 'unavailable';
  description: string;
  object_count: number | null;
  movement_type: string | null;
  movement_direction: string | null;
  duration_seconds: number | null;
  sound: string | null;
  flashing: boolean | null;
  trail: boolean | null;
  formation: boolean | null;
  confidence: string | null;
  weather: WeatherSnapshot | null;
  analysis: ReportAnalysis | null;
  matches: ReportMatch[];
  media: Array<{ id: string; type: 'photo' | 'video'; r2_key: string }>;
  corroborations: Corroboration[];
  comments: CaseComment[];
  is_reporter: boolean;
  /** Historic cases only: first-hand accounts paraphrased from published sources. */
  historical_accounts?: Array<{ name: string; date: string | null; text: string }>;
  /** Historic cases added from the admin: their source links. */
  sources?: Array<{ label: string; url: string }>;
}

export interface AuthUser { id: string; username: string; display_name?: string | null; created_at?: string; report_count?: number }

export interface ReportDraft {
  occurredAt: string;
  latitude: number | null;
  longitude: number | null;
  locationName: string;
  countryCode: string;
  description: string;
  objectCount: number | null;
  shape: string;
  colour: string;
  movementType: string;
  movementDirection: string;
  durationSeconds: number | null;
  sound: string;
  flashing: boolean | null;
}

export function isLegacyCase(item: Pick<CaseSummary, 'case_number' | 'occurred_at'> & { historic_title?: string }): boolean {
  if (item.historic_title) return true;
  if (
    item.case_number.startsWith('TU-19') ||
    item.case_number.startsWith('TU-200') ||
    item.case_number.startsWith('SS-19') ||
    item.case_number.startsWith('SS-200')
  ) {
    return true;
  }
  const year = new Date(item.occurred_at).getFullYear();
  return year < 2015;
}

// ---------------------------------------------------------------- news

export interface NewsSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featured_image: string | null;
  featured_image_alt: string | null;
  category: string;
  tags: string[];
  author: string;
  og_title: string | null;
  og_description: string | null;
  og_image: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
  created_at: string;
  updated_at: string;
}
export interface NewsPost extends NewsSummary { content: string }
export interface NewsFacet { name: string; count: number }
export interface NewsList {
  posts: NewsSummary[];
  hasMore: boolean;
  /** Total published posts matching the filter — drives numbered pagination. */
  total?: number | null;
  categories: NewsFacet[];
  tags: NewsFacet[];
}

// ---------------------------------------------------------------- tonight's sky

/** One visible satellite pass (GET /v1/sky/tonight). Times are UTC ISO instants. */
export interface TonightPassInfo {
  name: string;
  norad_id: number;
  kind: 'station' | 'starlink' | 'satellite';
  start: string;
  peak: string;
  end: string;
  max_elevation_deg: number;
  start_azimuth_deg: number;
  start_azimuth: string;
  peak_azimuth_deg: number;
  peak_azimuth: string;
  end_azimuth_deg: number;
  end_azimuth: string;
}

export interface TonightMoon {
  phase: string;
  illumination_pct: number;
  evaluated_at: string;
  /** Moonrise inside the night; null when the Moon doesn't rise before sunrise. */
  rise: string | null;
  /** Moonset inside the night; null likewise. */
  set: string | null;
  above_horizon_at_sunset: boolean;
}

/** Thresholds and omission counts the API applied — shown next to the data, never implied. */
export interface TonightCriteria {
  min_elevation_deg: number;
  sun_dark_below_deg: number;
  visibility_rule: string;
  catalogue_groups: string[];
  starlink_included: boolean;
  tle_epoch_max_age_days: number;
  max_passes: number;
  pass_selection: string;
  planets_rule: string;
  passes_total: number | null;
  passes_shown: number | null;
  below_elevation_omitted: number | null;
  stale_tle_omitted: number | null;
  catalogue_limit_omitted: number | null;
}

export interface TonightSky {
  night: {
    date: string | null;
    timezone: string | null;
    sunset: string;
    first_dark: string | null;
    last_dark: string | null;
    sunrise: string;
  } | null;
  night_unavailable?: string;
  criteria: TonightCriteria;
  passes: TonightPassInfo[] | null;
  passes_unavailable?: string;
  moon: TonightMoon | null;
  moon_unavailable?: string;
  planets: {
    evaluated_at: string;
    objects: Array<{ name: string; altitude_degrees: number; azimuth_degrees: number; magnitude: number }>;
  } | null;
  planets_unavailable?: string;
  forecast: {
    verdict: string;
    verdict_rule: string;
    verdict_hours: number;
    hours_in_window: number;
    timezone: string;
    source: string;
    fetched_at: string;
  } | null;
  forecast_unavailable?: string;
  sources: string[];
  computed_at: string;
}
